import type { ReactElement } from 'react';

/**
 * The product's visual signature, web side.
 *
 * The same idea as the native app's (`apps/mobile/src/components/brand.tsx`):
 * a set of RISING BARS, raked like a loaded boom. Drawn here as inline SVG
 * rather than divs because the web has one, and rendered from the same rake
 * angle and the same tokens so the two platforms read as one brand.
 *
 * There is no photography anywhere in this product and none is invented here.
 * The repository ships no image assets, and §11.2 deliberately withholds
 * machine photographs from the anonymous view — a stock excavator would be a
 * claim about equipment this platform does not have. Geometry says "industrial"
 * without asserting anything untrue.
 *
 * Every piece is decorative and carries no state, so all of it is
 * `aria-hidden`.
 */

const RAKE = -18;

/** The mark. Four bars, the tallest in the accent. */
export function BrandMark({ size = 28 }: { size?: number }): ReactElement {
  const bars = [0.45, 0.65, 0.85, 1];
  const width = size * 0.14;
  const gap = size * 0.1;
  const total = bars.length * width + (bars.length - 1) * gap;

  return (
    <svg
      className="brand-mark"
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${size}`}
      aria-hidden="true"
      focusable="false"
    >
      <g transform={`rotate(${RAKE} ${total / 2} ${size / 2})`}>
        {bars.map((scale, index) => (
          <rect
            key={scale}
            x={index * (width + gap)}
            y={size - size * scale}
            width={width}
            height={size * scale}
            rx={width / 3}
            fill={index === bars.length - 1 ? 'var(--accent)' : 'var(--ink)'}
            opacity={index === bars.length - 1 ? 1 : 0.25 + index * 0.12}
          />
        ))}
      </g>
    </svg>
  );
}

/** The mark beside the product name. */
export function BrandLockup(): ReactElement {
  return (
    <span className="brand-lockup">
      <BrandMark size={24} />
      <span className="brand-lockup__word">Equipment Rental</span>
    </span>
  );
}

/**
 * A faint field of raked bars for the space behind a hero.
 *
 * Static. A background that moves forever is decoration §7 rules out, and on a
 * long page it is animation the reader never asked for.
 */
export function MotifField({ height = 240 }: { height?: number }): ReactElement {
  const bars = [0.5, 0.78, 1, 0.62, 0.36, 0.84, 0.55];
  const width = 14;
  const gap = 22;
  const total = bars.length * (width + gap);

  return (
    <svg
      className="motif-field"
      viewBox={`0 0 ${total} ${height}`}
      preserveAspectRatio="xMaxYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <g transform={`rotate(${RAKE} ${total / 2} ${height / 2})`}>
        {bars.map((scale, index) => (
          <rect
            key={`${scale}-${index}`}
            x={index * (width + gap)}
            y={height - height * scale}
            width={width}
            height={height * scale}
            rx={width / 3}
            fill={index === 2 ? 'var(--accent)' : 'var(--ink)'}
            opacity={index === 2 ? 0.1 : 0.05}
          />
        ))}
      </g>
    </svg>
  );
}

/** A short accent rule. Marks where a section begins. */
export function MotifRule({ width = 40 }: { width?: number }): ReactElement {
  return <span className="motif-rule" style={{ width }} aria-hidden="true" />;
}

/**
 * Which half of the product you are in.
 *
 * Customer and owner shared one set of chrome, so nothing on screen told an
 * owner they were in the owner app. Same brand, named context.
 *
 * **`role: 'public'` is not a seat, and that distinction is the point.** The
 * equipment search is reachable without an account, and it was telling a
 * signed-out visitor they were "Renting" — a seat they had not taken, on a page
 * whose whole promise is that browsing needs no account. A visitor gets the
 * product's own context; a signed-in customer gets their seat, because then it
 * is true.
 */
export function RoleBanner({
  role,
  detail,
}: {
  role: 'customer' | 'owner' | 'public';
  detail?: string;
}): ReactElement {
  return (
    <p className="role-banner">
      <span className="role-banner__chip">
        <BrandMark size={14} />
        {role === 'owner'
          ? 'Owner workspace'
          : role === 'public'
            ? 'Equipment rental marketplace'
            : 'Renting'}
      </span>
      {detail === undefined ? null : <span className="role-banner__detail">{detail}</span>}
    </p>
  );
}

/**
 * The rental lifecycle, in three beats — the same three the native app shows.
 *
 * The landing page explains this product in four steps and the app never
 * repeated a word of it, which is how somebody could sign in without ever
 * learning what the product does.
 */
export const LIFECYCLE_BEATS = ['Find a machine', 'Agree the terms', 'Keep it on record'] as const;

export function LifecycleStrip(): ReactElement {
  return (
    <ol className="lifecycle-strip">
      {LIFECYCLE_BEATS.map((beat, index) => (
        <li key={beat} className="lifecycle-strip__beat">
          <span
            className={`lifecycle-strip__dot${index === 2 ? ' lifecycle-strip__dot--accent' : ''}`}
            aria-hidden="true"
          />
          {beat}
        </li>
      ))}
    </ol>
  );
}
